/**
 * PracticePlanCard Component
 *
 * Displays an evidence-based, motivating practice plan:
 * - Short weekly sessions (with explicit type + time) instead of an ambiguous
 *   "challenges per week" number
 * - Days until test countdown
 * - Encouraging feasibility assessment (never discouraging)
 * - Progress bar from current to goal
 * - Per-subject focus split
 * - A collapsible "Why this plan" panel explaining the reasoning + evidence
 */

import { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Target,
  Calendar,
  TrendingUp,
  Zap,
  RefreshCw,
  Clock,
  BarChart2,
  HelpCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react'

// SAT Score Constants
const SAT_TOTAL_MIN = 400

// Encouraging tones only — there is deliberately no red/"unlikely" state.
const FEASIBILITY_STYLES = {
  achieved: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    text: 'text-green-700 dark:text-green-300'
  },
  comfortable: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    text: 'text-green-700 dark:text-green-300'
  },
  'on-track': {
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-200 dark:border-emerald-800',
    text: 'text-emerald-700 dark:text-emerald-300'
  },
  stretch: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
    text: 'text-amber-700 dark:text-amber-300'
  },
  ambitious: {
    bg: 'bg-indigo-50 dark:bg-indigo-900/20',
    border: 'border-indigo-200 dark:border-indigo-800',
    text: 'text-indigo-700 dark:text-indigo-300'
  }
}

const PracticePlanCard = ({ data, onUpdateGoal, onStartPractice, readOnly = false }) => {
  const [showWhy, setShowWhy] = useState(false)

  if (!data) return null

  // A stored test date that has already passed yields a degraded payload (no plan numbers).
  // Show a focused "update your test date" prompt instead of a plan full of blanks.
  if (data.testDatePassed) {
    return (
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary-600" />
            Your Practice Plan
          </h2>
        </div>
        <div className="card-body text-center py-6 space-y-4">
          <div className="w-14 h-14 bg-amber-50 dark:bg-amber-900/20 rounded-full flex items-center justify-center mx-auto">
            <Clock className="w-7 h-7 text-amber-600 dark:text-amber-400" />
          </div>
          <p className="text-gray-600 dark:text-gray-400 max-w-sm mx-auto">
            Your test date has passed. Update it to get a fresh, personalized practice plan.
          </p>
          {!readOnly && onUpdateGoal && (
            <button onClick={onUpdateGoal} className="btn-primary inline-flex items-center gap-2">
              <Target className="w-4 h-4" />
              Update Test Date
            </button>
          )}
        </div>
      </div>
    )
  }

  const {
    currentScore,
    goalScore,
    pointsNeeded,
    daysUntilTest,
    recommendedSessionsPerWeek,
    sessionMinutes = 20,
    estimatedMinutesPerWeek,
    fullChallengeEquivalentPerWeek,
    totalEstimatedHours,
    feasibility,
    breakdown,
    reasoning,
    progressSinceGoalSet,
    hasTestDateSet
  } = data

  const feasibilityStyle = FEASIBILITY_STYLES[feasibility?.level] || FEASIBILITY_STYLES['on-track']
  const progressPercent = Math.min(
    100,
    Math.max(0, ((currentScore - SAT_TOTAL_MIN) / (goalScore - SAT_TOTAL_MIN)) * 100)
  )

  const sessionsLabel = recommendedSessionsPerWeek === 1 ? 'session' : 'sessions'
  const weeklyHours =
    estimatedMinutesPerWeek != null ? Math.round((estimatedMinutesPerWeek / 60) * 10) / 10 : null

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary-600" />
          Your Practice Plan
        </h2>
        {progressSinceGoalSet && (
          <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <RefreshCw className="w-3 h-3" />
            Updates as you practice
          </span>
        )}
      </div>

      <div className="card-body space-y-5">
        {/* Main Stats - weekly sessions and days until test */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-xl p-4 text-center">
            <div className="text-4xl font-bold text-primary-600 dark:text-primary-400">
              {recommendedSessionsPerWeek ?? '—'}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              quick {sessionsLabel} / week
            </div>
            {recommendedSessionsPerWeek > 0 && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                ~{sessionMinutes} min each
                {weeklyHours != null ? ` · ~${weeklyHours} hr/week` : ''}
              </div>
            )}
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4 text-center">
            <div className="text-4xl font-bold text-purple-600 dark:text-purple-400">
              {daysUntilTest || '—'}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {hasTestDateSet ? 'days until test' : 'days (default)'}
            </div>
          </div>
        </div>

        {/* Full-challenge equivalent hint */}
        {recommendedSessionsPerWeek > 0 && fullChallengeEquivalentPerWeek > 0 && (
          <p className="text-xs text-center text-gray-500 dark:text-gray-400">
            Prefer longer sits? That&apos;s about{' '}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {fullChallengeEquivalentPerWeek}
            </span>{' '}
            full challenge{fullChallengeEquivalentPerWeek === 1 ? '' : 's'} a week.
          </p>
        )}

        {/* Progress bar - Current to Goal */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              Current:{' '}
              <span className="font-semibold text-gray-900 dark:text-gray-100">{currentScore}</span>
            </span>
            <span className="text-gray-600 dark:text-gray-400">
              Goal:{' '}
              <span className="font-semibold text-primary-600 dark:text-primary-400">
                {goalScore}
              </span>
            </span>
          </div>
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="text-center text-sm">
            {pointsNeeded > 0 ? (
              <span className="text-gray-600 dark:text-gray-400">
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  +{pointsNeeded}
                </span>{' '}
                points to go
              </span>
            ) : (
              <span className="text-green-600 dark:text-green-400 font-semibold">
                You&apos;ve reached your goal!
              </span>
            )}
          </div>
        </div>

        {/* Feasibility Assessment (always encouraging) */}
        <div className={`p-3 rounded-lg border ${feasibilityStyle.bg} ${feasibilityStyle.border}`}>
          <div className="flex items-center gap-2">
            <span className="text-xl">{feasibility?.emoji || '✅'}</span>
            <div>
              <span className={`font-medium capitalize ${feasibilityStyle.text}`}>
                {feasibility?.level ? feasibility.level.replace('-', ' ') : 'On track'}
              </span>
              <p className={`text-sm ${feasibilityStyle.text} opacity-80`}>
                {feasibility?.message || 'Keep practicing to see your progress!'}
              </p>
            </div>
          </div>
        </div>

        {/* Why this plan? — reasoning + evidence */}
        {reasoning && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setShowWhy((v) => !v)}
              aria-expanded={showWhy}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-lg"
            >
              <span className="flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                Why this plan?
              </span>
              {showWhy ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showWhy && (
              <div className="px-4 pb-3 pt-1 space-y-2">
                <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
                  {(reasoning.steps || []).map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
                {(totalEstimatedHours || reasoning.totalEstimatedHours) && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Estimated total practice to reach your goal:{' '}
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      ~{totalEstimatedHours ?? reasoning.totalEstimatedHours} hours
                    </span>
                    .
                  </p>
                )}
                {reasoning.citation && (
                  <p className="text-xs italic text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-700 pt-2">
                    {reasoning.citation}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Per-Subject Focus split */}
        {breakdown && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
              <BarChart2 className="w-4 h-4" />
              This Week&apos;s Focus
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Math</span>
                  <span className="text-xs text-blue-600 dark:text-blue-400">
                    +{breakdown.mathPointsNeeded || 0} pts
                  </span>
                </div>
                <div className="text-lg font-bold text-blue-800 dark:text-blue-200 mt-1">
                  {breakdown.mathSessionsPerWeek != null
                    ? breakdown.mathSessionsPerWeek
                    : Math.ceil((recommendedSessionsPerWeek || 0) / 2)}{' '}
                  {sessionsLabel}
                </div>
              </div>
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                    R&amp;W
                  </span>
                  <span className="text-xs text-purple-600 dark:text-purple-400">
                    +{breakdown.rwPointsNeeded || 0} pts
                  </span>
                </div>
                <div className="text-lg font-bold text-purple-800 dark:text-purple-200 mt-1">
                  {breakdown.rwSessionsPerWeek != null
                    ? breakdown.rwSessionsPerWeek
                    : Math.floor((recommendedSessionsPerWeek || 0) / 2)}{' '}
                  {sessionsLabel}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Progress Since Goal Set */}
        {progressSinceGoalSet && progressSinceGoalSet.challengesCompleted > 0 && (
          <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
              <TrendingUp className="w-4 h-4" />
              Since you set your goal:
            </h4>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div
                  className={`text-lg font-bold ${progressSinceGoalSet.pointsGained >= 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}
                >
                  {progressSinceGoalSet.pointsGained >= 0 ? '+' : ''}
                  {progressSinceGoalSet.pointsGained}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">points</div>
              </div>
              <div>
                <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {progressSinceGoalSet.challengesCompleted}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">challenges</div>
              </div>
              <div>
                <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {progressSinceGoalSet.weeksElapsed}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">weeks</div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons (hidden in read-only mode) */}
        {!readOnly && (onStartPractice || onUpdateGoal) && (
          <div className="flex gap-2 pt-2">
            {onStartPractice && (
              <button
                onClick={onStartPractice}
                className="flex-1 btn-primary flex items-center justify-center gap-2"
              >
                <Zap className="w-4 h-4" />
                Start Practice
              </button>
            )}
            {onUpdateGoal && (
              <button
                onClick={onUpdateGoal}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-1"
              >
                <Target className="w-4 h-4" />
                Update Goal
              </button>
            )}
          </div>
        )}

        {/* No test date warning (hidden in read-only mode) */}
        {!readOnly && !hasTestDateSet && (
          <p className="text-xs text-center text-gray-500 dark:text-gray-400">
            <Clock className="w-3 h-3 inline mr-1" />
            Set your test date for a more accurate practice plan
          </p>
        )}
      </div>
    </div>
  )
}

PracticePlanCard.propTypes = {
  data: PropTypes.shape({
    currentScore: PropTypes.number,
    goalScore: PropTypes.number,
    pointsNeeded: PropTypes.number,
    daysUntilTest: PropTypes.number,
    weeksUntilTest: PropTypes.number,
    recommendedSessionsPerWeek: PropTypes.number,
    sessionType: PropTypes.string,
    sessionMinutes: PropTypes.number,
    estimatedMinutesPerWeek: PropTypes.number,
    estimatedQuestionsPerWeek: PropTypes.number,
    fullChallengeEquivalentPerWeek: PropTypes.number,
    totalEstimatedHours: PropTypes.number,
    // Back-compat alias
    recommendedChallengesPerWeek: PropTypes.number,
    feasibility: PropTypes.shape({
      level: PropTypes.string,
      emoji: PropTypes.string,
      message: PropTypes.string
    }),
    breakdown: PropTypes.shape({
      mathPointsNeeded: PropTypes.number,
      rwPointsNeeded: PropTypes.number,
      mathSessionsPerWeek: PropTypes.number,
      rwSessionsPerWeek: PropTypes.number
    }),
    reasoning: PropTypes.shape({
      steps: PropTypes.arrayOf(PropTypes.string),
      citation: PropTypes.string,
      totalEstimatedHours: PropTypes.number
    }),
    progressSinceGoalSet: PropTypes.shape({
      pointsGained: PropTypes.number,
      challengesCompleted: PropTypes.number,
      weeksElapsed: PropTypes.number
    }),
    hasTestDateSet: PropTypes.bool,
    testDatePassed: PropTypes.bool
  }),
  onUpdateGoal: PropTypes.func,
  onStartPractice: PropTypes.func,
  readOnly: PropTypes.bool
}

export default PracticePlanCard
